import React from "react";

/*
 * Shared presentation for long-form legal pages (Privacy Policy, Terms).
 * Plain anchors only — no router dependency, no scroll logic.
 */

export interface LegalTocItem {
  id: string;
  label: React.ReactNode;
}

interface LegalPageProps {
  id: string;
  title: React.ReactNode;
  titleTestId: string;
  toc?: LegalTocItem[];
  children: React.ReactNode;
}

export const LegalPage = ({ id, title, titleTestId, toc, children }: LegalPageProps) => (
  <div id={id} className="scroll-mt-24 bg-canvas">
    <header className="border-b bg-surface border-line">
      <div className="px-4 py-12 mx-auto max-w-7xl sm:px-6 sm:py-16 lg:px-8">
        <p className="zs-eyebrow">Legal</p>
        <h1
          data-testid={titleTestId}
          className="max-w-3xl mt-1 text-3xl font-bold tracking-tight font-display text-ink sm:text-5xl"
        >
          {title}
        </h1>
      </div>
    </header>

    <div className="px-4 py-12 mx-auto max-w-7xl sm:px-6 sm:py-16 lg:px-8 lg:grid lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-16">
      {toc && toc.length > 0 ? (
        <aside className="hidden lg:block">
          <nav aria-label="On this page" className="sticky top-28 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2">
            <p className="mb-3 text-xs font-semibold tracking-wider uppercase text-ink">On this page</p>
            <ul className="border-l border-line">
              {toc.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className="block py-1.5 pl-4 -ml-px text-[13px] leading-snug border-l border-transparent text-ink-3 transition-colors hover:text-ink hover:border-ink-3"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </aside>
      ) : (
        <div className="hidden lg:block" />
      )}

      <article className="max-w-[72ch] text-[15px] leading-7 text-ink-2">{children}</article>
    </div>
  </div>
);

/** A top-level section with an anchor that clears the fixed header. */
export const LegalSection = ({
  id,
  title,
  children,
}: {
  id?: string;
  title: React.ReactNode;
  children?: React.ReactNode;
}) => (
  <section id={id} className="pt-10 mt-10 border-t scroll-mt-24 first:mt-0 first:pt-0 first:border-t-0 border-line">
    <h2 className="mb-4 text-xl font-semibold tracking-tight font-display text-ink sm:text-2xl">{title}</h2>
    <div className="space-y-4">{children}</div>
  </section>
);

/** Sub-heading inside a section (e.g. individual rights). */
export const LegalSubheading = ({ children }: { children: React.ReactNode }) => (
  <h3 className="pt-2 text-base font-semibold text-ink">{children}</h3>
);

/** Bulleted (default) or numbered list of clauses. */
export const LegalList = ({ items, ordered = false }: { items: React.ReactNode[]; ordered?: boolean }) => {
  const Tag = ordered ? "ol" : "ul";
  return (
    <Tag className={`space-y-2 pl-5 ${ordered ? "list-decimal" : "list-disc"} marker:text-ink-4`}>
      {items.map((item, index) => (
        <li key={index} className="pl-1">
          {item}
        </li>
      ))}
    </Tag>
  );
};

export const legalLinkClass = "zs-link break-words";
